/**
 * FRIDAY-34: move a board or a page (with its sub-pages) to another workspace.
 *
 * Preview is a POST that writes nothing; execute sends the preview's
 * `expectedVersion` back with an Idempotency-Key. Both workspaces' caches are
 * invalidated after a move.
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export type TransferKind = 'project' | 'page'

export interface TransferPreview {
  kind: TransferKind
  id: string
  label: string
  sourceWorkspaceId: string
  targetWorkspaceId: string
  targetWorkspaceName: string
  targetParentPageId: string | null
  counts: Record<string, number>
  usersLosingAccess: { count: number; users: { userId: string; displayName: string | null }[] }
  removals: Record<string, number>
  detach: Record<string, number>
  slugRenames: { pageId: string; from: string; to: string }[]
  depth: { deepestAfterMove: number; max: number } | null
  size: { rowsWritten: number; limit: number }
  expectedVersion: string
}

export interface TransferResult {
  status: 'moved' | 'already_moved'
  kind: TransferKind
  id: string
  sourceWorkspaceId: string | null
  targetWorkspaceId: string
  path: string
  summary: Record<string, unknown> | null
}

export interface TransferTarget {
  targetWorkspaceId: string
  targetParentPageId?: string | null
}

function base(kind: TransferKind, id: string) {
  return kind === 'project' ? `/api/v1/projects/${id}/transfer` : `/api/v1/pages/${id}/transfer`
}

export function useTransferPreview(kind: TransferKind, id: string, target: TransferTarget | null) {
  return useQuery({
    queryKey: ['transfer-preview', kind, id, target?.targetWorkspaceId, target?.targetParentPageId ?? null],
    queryFn: () =>
      api<TransferPreview>(`${base(kind, id)}/preview`, {
        method: 'POST',
        body: {
          targetWorkspaceId: target!.targetWorkspaceId,
          ...(kind === 'page' ? { targetParentPageId: target!.targetParentPageId ?? null } : {}),
        },
      }),
    enabled: !!target?.targetWorkspaceId,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  })
}

/** Drop every cached query that could still show the item in either workspace. */
export function invalidateAfterTransfer(qc: QueryClient, workspaceIds: (string | null | undefined)[]) {
  void qc.invalidateQueries({ queryKey: ['workspaces'] })
  void qc.invalidateQueries({ queryKey: ['project-by-key'] })
  void qc.invalidateQueries({ queryKey: ['board'] })
  void qc.invalidateQueries({ queryKey: ['task'] })
  void qc.invalidateQueries({ queryKey: ['my-tasks'] })
  void qc.invalidateQueries({ queryKey: ['page'] })
  void qc.invalidateQueries({ queryKey: ['task-page-refs'] })
  void qc.invalidateQueries({ queryKey: ['page-tasks'] })
  for (const wsId of workspaceIds) {
    if (!wsId) continue
    void qc.invalidateQueries({ queryKey: ['workspaces', wsId, 'projects'] })
    void qc.invalidateQueries({ queryKey: ['workspace-overview', wsId] })
    void qc.invalidateQueries({ queryKey: ['page-tree', wsId] })
  }
}

export function useExecuteTransfer(kind: TransferKind, id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: TransferTarget & { expectedVersion: string; idemKey: string }) =>
      api<TransferResult>(`${base(kind, id)}/execute`, {
        method: 'POST',
        idemKey: input.idemKey,
        body: {
          targetWorkspaceId: input.targetWorkspaceId,
          expectedVersion: input.expectedVersion,
          ...(kind === 'page' ? { targetParentPageId: input.targetParentPageId ?? null } : {}),
        },
      }),
    onSuccess: (result) => invalidateAfterTransfer(qc, [result.sourceWorkspaceId, result.targetWorkspaceId]),
  })
}

/** Where a board key lives now, for old `/w/:wsId/p/:key` links. 404 when not visible. */
export function useResolveProjectKey(key: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['project-key-home', key],
    queryFn: () => api<{ id: string; workspaceId: string; key: string }>(`/api/v1/me/projects/by-key/${key}`),
    enabled: !!key && enabled,
    retry: false,
  })
}
